import { PlusOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { CreditCardIcon, NumberIcon } from 'assets/images/svg';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCategoriesByCategoryID } from 'reduxStore/categories/selectors';
import { selectDevices } from 'reduxStore/devices/selectors';
import { Button, Col, Divider, Drawer, Flex, Row } from 'zui/Antd';
import { InfoTuple } from 'zui/Custom';
import { Loader } from 'zComponents/atoms';
import {
	AddCategoryFields,
	AdditionalInformationPanelContent,
	CredentialDrawerSkeleton,
	EditDrawerHeader
} from './components';
import ManageCategoryDropdown from '../ManageCategoryDropdown';

const CategoryDrawer = ({ visible: visibleProp, setVisible: setVisibleProp, categoryID }) => {
	// ✅ Hooks
	const {
		token: { paddingXXL = 48, paddingLG, borderRadiusLG, paddingMD, paddingSM, paddingXL }
	} = theme.useToken();

	// ✅ Redux state
	const category = useSelector((state) => selectCategoriesByCategoryID(state, categoryID));
	const devices = useSelector(selectDevices);
	const { loadingDeleteCategoryField, loadingCreateCategoryField, loadingUpdateCategoryField } = useSelector(
		(state) => state.Categories
	);

	// ✅ Local state
	const [addField, setAddField] = useState(false);
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;
	const loading = loadingDeleteCategoryField || loadingCreateCategoryField || loadingUpdateCategoryField;

	const handleOnClose = () => {
		setVisible(false);
		setAddField(false);
	};

	const drawerStyles = useMemo(
		() => ({
			wrapper: { margin: '30px 20px 30px 0px' },
			content: { borderRadius: borderRadiusLG },
			body: { padding: paddingXXL }
		}),
		[borderRadiusLG, paddingXXL]
	);

	const deviceCount = useMemo(() => {
		if (!category || !devices) return 0;
		return devices.filter((device) => device.categoryID === categoryID).length;
	}, [category, categoryID, devices]);

	// Check if category is editable based on defaultCategoryID
	const isEditable = useMemo(() => !category?.defaultCategoryID, [category?.defaultCategoryID]);

	return (
		<Drawer
			placement='right'
			onClose={handleOnClose}
			open={visible}
			width={871}
			closeIcon={false}
			destroyOnClose
			styles={drawerStyles}
		>
			{!category ? (
				<CredentialDrawerSkeleton />
			) : (
				<Flex vertical>
					<EditDrawerHeader
						categoryID={categoryID}
						defaultCategoryID={category?.defaultCategoryID}
						name={category.name || 'Untitled Category'}
						description={category.description || '-'}
						handleOnCloseEdit={handleOnClose}
					/>

					<div
						style={{
							marginTop: paddingXXL
						}}
					>
						<ManageCategoryDropdown categoryID={categoryID} />
					</div>
					<Divider style={{ margin: `${paddingMD}px 0px` }} />
					<Row style={{ marginBottom: paddingXL }}>
						<Col span={24}>
							<Flex vertical gap={paddingLG} style={{ padding: `${paddingSM}px ${paddingSM}px 0px` }}>
								<InfoTuple icon={<NumberIcon />} text='Devices' value={deviceCount} />
								<InfoTuple icon={<CreditCardIcon />} text='Type' value={category?.typeName} />
							</Flex>
						</Col>
					</Row>

					{/* Additional Information Section */}
					{loading ? (
						<div style={{ padding: `${paddingXL}px 0` }}>
							<Loader />
						</div>
					) : (
						<Flex vertical gap={paddingSM} style={{ padding: `0px  ${paddingSM}px` }}>
							<AdditionalInformationPanelContent categoryID={categoryID} />
							{/* Add Custom Field Button */}
							{isEditable && !addField && (
								<Button type='dashed' icon={<PlusOutlined />} onClick={() => setAddField(true)} block>
									Add Custom Field
								</Button>
							)}
							{addField && (
								<AddCategoryFields
									categoryID={categoryID}
									setAddField={setAddField}
									setIsEditing={() => {}}
								/>
							)}
						</Flex>
					)}
				</Flex>
			)}
		</Drawer>
	);
};

export default CategoryDrawer;
